import { EditorStarterItem } from '../../../items/item-type';
import { TrackType } from '../../../state/types';
import { removeEmptyTracks } from '../../../utils/remove-empty-tracks';
import { getZoneForItemType } from '../../../utils/zone-utils';
import {
  DragPreviewState,
  PreviewPosition,
  PushedBackItemPreview,
} from '../../drag-preview-provider';
import { insertNewTracks } from './insert-new-tracks';
import { TrackInsertions } from './types';

const calculateOriginalTrackIndex = ({
  currentIndex,
  trackInsertions,
}: {
  currentIndex: number;
  trackInsertions: TrackInsertions | null;
}): number => {
  if (!trackInsertions) {
    return currentIndex;
  }

  switch (trackInsertions.type) {
    case 'top':
      return currentIndex - trackInsertions.count;
    case 'bottom':
      return currentIndex;
    case 'between':
      if (currentIndex < trackInsertions.trackIndex) {
        // 삽입 지점 이전의 track들은 원래 index를 유지
        return currentIndex;
      }
      if (currentIndex < trackInsertions.trackIndex + trackInsertions.count) {
        // 새로운 track들은 원래 index가 없음
        return -1;
      }
      // 삽입 지점 이후의 track들은 뒤로 이동
      return currentIndex - trackInsertions.count;
    default:
      throw new Error(
        `Unexpected trackInsertions: ${JSON.stringify(trackInsertions satisfies never)}`,
      );
  }
};

const getTrackItemsWithoutDraggedOnes = ({
  originalIdx,
  prevTracks,
  itemsToDrag,
}: {
  originalIdx: number;
  prevTracks: TrackType[];
  itemsToDrag: string[];
}): string[] => {
  if (originalIdx < 0 || originalIdx >= prevTracks.length) {
    return [];
  }

  const track = prevTracks[originalIdx];
  if (!track) {
    return [];
  }

  return track.items.filter((itemId) => !itemsToDrag.includes(itemId));
};

const buildTrackItemsMap = ({
  expandedTracks,
  prevTracks,
  itemsToDrag,
  trackInsertions,
}: {
  expandedTracks: TrackType[];
  prevTracks: TrackType[];
  itemsToDrag: string[];
  trackInsertions: TrackInsertions | null;
}): Map<number, string[]> => {
  const trackItemsMap = new Map<number, string[]>();

  for (let idx = 0; idx < expandedTracks.length; idx++) {
    const originalIdx = calculateOriginalTrackIndex({
      currentIndex: idx,
      trackInsertions,
    });
    const trackItems = getTrackItemsWithoutDraggedOnes({
      originalIdx,
      prevTracks,
      itemsToDrag,
    });
    trackItemsMap.set(idx, trackItems);
  }

  return trackItemsMap;
};

const applyPositionsToMaps = ({
  trackItemsMap,
  items,
  newPositions,
}: {
  trackItemsMap: Map<number, string[]>;
  items: Record<string, EditorStarterItem>;
  newPositions: PreviewPosition[];
}): Record<string, EditorStarterItem> => {
  const newItems: Record<string, EditorStarterItem> = { ...items };

  for (const newPos of newPositions) {
    const trackItems = trackItemsMap.get(newPos.trackIndex) || [];
    trackItems.push(newPos.id);
    trackItemsMap.set(newPos.trackIndex, trackItems);

    const existingItem = items[newPos.id];
    if (!existingItem) continue;
    newItems[newPos.id] = {
      ...existingItem,
      from: newPos.from,
      durationInFrames: newPos.durationInFrames,
      isDraggingInTimeline: false,
    } as EditorStarterItem;
  }

  return newItems;
};

/**
 * Magnetic 트랙에서 밀려나는 아이템들의 위치를 적용합니다.
 * 실제 상태를 변경하지 않고 렌더링용 데이터만 업데이트합니다.
 */
const applyPushedBackPositions = ({
  items,
  pushedBackItems,
}: {
  items: Record<string, EditorStarterItem>;
  pushedBackItems?: PushedBackItemPreview[];
}): Record<string, EditorStarterItem> => {
  if (!pushedBackItems || pushedBackItems.length === 0) {
    return items;
  }

  const updatedItems = { ...items };

  for (const pushed of pushedBackItems) {
    const existingItem = updatedItems[pushed.id];
    if (!existingItem) continue;

    updatedItems[pushed.id] = {
      ...existingItem,
      from: pushed.newFrom, // 새 위치로 업데이트
    };
  }

  return updatedItems;
};

/**
 * Rebuilds tracks from the track items map, sorting items by position
 */
const rebuildTracksFromMap = ({
  expandedTracks,
  trackItemsMap,
  newItems,
}: {
  expandedTracks: TrackType[];
  trackItemsMap: Map<number, string[]>;
  newItems: Record<string, EditorStarterItem>;
}): TrackType[] => {
  return expandedTracks.map((track, idx) => {
    const trackItems = trackItemsMap.get(idx);
    if (!trackItems) {
      throw new Error(
        `Track items not found for index: ${idx}, expandedTracks length: ${expandedTracks.length}`,
      );
    }

    trackItems.sort((a, b) => {
      const itemA = newItems[a];
      const itemB = newItems[b];
      if (!itemA || !itemB) return 0;
      return itemA.from - itemB.from;
    });

    return {
      ...track,
      items: trackItems,
    };
  });
};

export const applyNewPositionsToState = ({
  prevTracks,
  dragPreview,
  prevItems,
  shouldRemoveEmptyTracks,
}: {
  prevTracks: TrackType[];
  dragPreview: DragPreviewState;
  prevItems: Record<string, EditorStarterItem>;
  shouldRemoveEmptyTracks: boolean;
}): { tracks: TrackType[]; items: Record<string, EditorStarterItem> } => {
  const firstDraggedItemId = dragPreview.itemsBeingDragged[0];
  const insertionZone =
    firstDraggedItemId && prevItems[firstDraggedItemId]
      ? getZoneForItemType(prevItems[firstDraggedItemId].type)
      : 'visual';

  const expandedTracks = insertNewTracks({
    tracks: prevTracks,
    trackInsertions: dragPreview.trackInsertions,
    zone: insertionZone,
  });

  const trackItemsMap = buildTrackItemsMap({
    expandedTracks,
    prevTracks,
    itemsToDrag: dragPreview.itemsBeingDragged,
    trackInsertions: dragPreview.trackInsertions,
  });

  const newItems = applyPositionsToMaps({
    trackItemsMap,
    items: prevItems,
    newPositions: dragPreview.positions,
  });

  // Magnetic 트랙에서 밀려나는 아이템들의 위치도 적용 (렌더링용)
  const itemsWithPushedBack = applyPushedBackPositions({
    items: newItems,
    pushedBackItems: dragPreview.pushedBackItems,
  });

  const newTracks = rebuildTracksFromMap({
    expandedTracks,
    trackItemsMap,
    newItems: itemsWithPushedBack,
  });

  const finalTracks = shouldRemoveEmptyTracks
    ? removeEmptyTracks(newTracks)
    : newTracks;

  return {
    tracks: finalTracks,
    items: itemsWithPushedBack,
  };
};
